"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo, useState } from "react";
import type { Key } from "react";
import { DataGrid, type SortDirection } from "@xefi/x-react/datagrid";
import { useTranslation } from "react-i18next";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useSearchPending } from "@/hooks/useSearchPending";
import { useTelecollections } from "@/hooks/useTelecollections";
import { usePaymentsDateRange, usePaymentsDomaIds, usePaymentsSubsidiaryId } from "@/store/usePaymentsFiltersStore";
import { usePaymentsSearchStore, usePaymentsSearchTerm } from "@/store/usePaymentsSearchStore";
import type { PaymentsSortConfig } from "@/types/payments";
import { isForbiddenError } from "@/utils/apiError";
import ExportButton from "../common/ExportButton";
import PaymentsSearchInput from "../common/PaymentsSearchInput";
import { getSharedFiltersSignature, toSearchFilter, toTableFilters } from "../common/paymentsQuery";
import { DEFAULT_TELECOLLECTION_FIELDS, useTelecollectionsColumns } from "./telecollectionsColumns";
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];
const DEFAULT_ROWS_PER_PAGE = 25;
const SEARCH_FIELDS = ["authorization_number", "site_label", "application_name", "deposit_number", "tpv"];
const AMOUNT_SEARCH_FIELDS = ["amount"];
export default function TelecollectionsTable() {
  const $ = _c(43);
  const {
    t
  } = useTranslation();
  const subsidiaryId = usePaymentsSubsidiaryId();
  const domaIds = usePaymentsDomaIds();
  const dateRange = usePaymentsDateRange();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [sort, setSort] = useState(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = new Set(DEFAULT_TELECOLLECTION_FIELDS);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const visibleFields = t0;
  const search = usePaymentsSearchTerm("telecollections");
  const setSearchTerm = usePaymentsSearchStore(_temp);
  const debouncedSearch = useDebouncedValue(search);
  let t1;
  if ($[1] !== dateRange || $[2] !== debouncedSearch || $[3] !== domaIds || $[4] !== subsidiaryId) {
    const base = toTableFilters({
      subsidiaryId,
      domaIds,
      dateRange
    }, {
      field: "transaction_at",
      kind: "datetime"
    });
    let t2;
    if ($[6] !== debouncedSearch) {
      t2 = toSearchFilter(debouncedSearch, SEARCH_FIELDS, AMOUNT_SEARCH_FIELDS);
      $[6] = debouncedSearch;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    const searchFilter = t2;
    t1 = searchFilter ? [...base, searchFilter] : base;
    $[1] = dateRange;
    $[2] = debouncedSearch;
    $[3] = domaIds;
    $[4] = subsidiaryId;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const filters = t1;
  let t2;
  if ($[8] !== dateRange || $[9] !== domaIds || $[10] !== subsidiaryId) {
    t2 = getSharedFiltersSignature({
      subsidiaryId,
      domaIds,
      dateRange
    });
    $[8] = dateRange;
    $[9] = domaIds;
    $[10] = subsidiaryId;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const filtersSignature = `${t2}|${debouncedSearch}`;
  const [previousSignature, setPreviousSignature] = useState(filtersSignature);
  if (filtersSignature !== previousSignature) {
    setPreviousSignature(filtersSignature);
    setPage(1);
  }
  const columns = useTelecollectionsColumns(visibleFields);
  let t3;
  if ($[12] !== sort) {
    t3 = sort ? [sort] : undefined;
    $[12] = sort;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== filters || $[15] !== page || $[16] !== rowsPerPage || $[17] !== t3) {
    t4 = {
      filters,
      sorts: t3,
      page,
      limit: rowsPerPage
    };
    $[14] = filters;
    $[15] = page;
    $[16] = rowsPerPage;
    $[17] = t3;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  const {
    data,
    total,
    isLoading,
    isFetching,
    error
  } = useTelecollections(t4);
  const isSearching = useSearchPending(debouncedSearch, isFetching);
  let t5;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = (column, direction) => {
      setSort({
        field: String(column),
        direction
      });
      setPage(1);
    };
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const handleSortChange = t5;
  let t6;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = nextRowsPerPage => {
      setRowsPerPage(nextRowsPerPage);
      setPage(1);
    };
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const handleRowsPerPageChange = t6;
  let t7;
  if ($[21] !== setSearchTerm) {
    t7 = value => setSearchTerm("telecollections", value);
    $[21] = setSearchTerm;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== search || $[24] !== t7) {
    t8 = <PaymentsSearchInput value={search} onChange={t7} />;
    $[23] = search;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <ExportButton category="telecollections" />;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== t8) {
    t10 = <div className="flex items-center justify-end gap-2">{t8}{t9}</div>;
    $[27] = t8;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== columns || $[30] !== data || $[31] !== error || $[32] !== isFetching || $[33] !== isLoading || $[34] !== isSearching || $[35] !== page || $[36] !== rowsPerPage || $[37] !== t || $[38] !== total) {
    t11 = error ? <div className="flex h-[24vh] items-center justify-center rounded-xl border border-border dark:border-[#3a3a3a]"><p className="text-sm text-danger">{isForbiddenError(error) ? t("paymentsReceived.states.forbidden") : t("paymentsReceived.states.error")}</p></div> : <DataGrid className="w-full" rows={data} columns={columns} isLoading={isLoading || isSearching} isFetching={isFetching} childrenProps={{
      tableBodyProps: {
        emptyContent: <div className="flex h-[18vh] items-center justify-center"><p className="text-lg text-[#666666] dark:text-[#d1cfcf]">{t("paymentsReceived.telecollections.empty")}</p></div>
      }
    }} onSortChange={handleSortChange} paginationType="paginated" currentPage={page} totalItems={total} rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS} defaultRowsPerPage={rowsPerPage} onFetchPage={setPage} onRowsPerPageChange={handleRowsPerPageChange} />;
    $[29] = columns;
    $[30] = data;
    $[31] = error;
    $[32] = isFetching;
    $[33] = isLoading;
    $[34] = isSearching;
    $[35] = page;
    $[36] = rowsPerPage;
    $[37] = t;
    $[38] = total;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  let t12;
  if ($[40] !== t10 || $[41] !== t11) {
    t12 = <div className="flex flex-col gap-3">{t10}{t11}</div>;
    $[40] = t10;
    $[41] = t11;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  return t12;
}
function _temp(state) {
  return state.setTerm;
}